import { Expr, MonoFactor, QuerySpec, SpecFactor, parseExpression } from "./parser.js";
import {
  Quantity,
  DimMap,
  CalcError,
  cloneDims,
  mul,
  div,
  add,
  signature,
} from "./model.js";
import { resolveUnit, matchData, baseOf, tagOf, nameOf, TIME } from "./units.js";

// ---- dim helpers -----------------------------------------------------------

function addExp(d: DimMap, key: string, delta: number): void {
  const next = (d.get(key) ?? 0) + delta;
  if (next === 0) d.delete(key);
  else d.set(key, next);
}

// ---- build a quantity from a literal node ----------------------------------

export function buildQuantity(node: Extract<Expr, { t: "q" }>): Quantity {
  let value = node.value * node.mag;
  const dims: DimMap = new Map();
  let time: string | undefined;

  for (const f of node.mono) {
    const u = resolveUnit(f.unit);
    const exp = f.op === "*" ? 1 : -1;
    value *= u.factor ** exp;
    const key = u.dim + (f.tag ? "#" + f.tag : "");
    addExp(dims, key, exp);
    if (u.family === "time" && exp < 0) time = f.unit; // mentioned rate denominator
  }

  const q: Quantity = { value, dims };
  if (time) q.time = time;
  return q;
}

export function evalExpr(node: Expr): Quantity {
  if (node.t === "q") return buildQuantity(node);
  const l = evalExpr(node.l);
  const r = evalExpr(node.r);
  switch (node.op) {
    case "*":
      return mul(l, r);
    case "/":
      return div(l, r);
    case "+":
      return add(l, r, "+");
    case "-":
      return add(l, r, "-");
  }
}

// ---- facts store -----------------------------------------------------------

export class Facts {
  /** Precise mode (`={}`): render to two significant figures instead of one. */
  constructor(readonly precise = false) {}
  /** pivot dimKey → list of expansions (each: "1 pivot = this quantity"). */
  private map = new Map<string, Quantity[]>();
  /** dim → stated absolute count ("400 users" → user = 400). Last statement wins. */
  private pops = new Map<string, Quantity>();
  /** numerator dim → stated flow rate ("100m urls/mo" → url at 100m/mo). Last wins. */
  private flowMap = new Map<string, Quantity>();
  /** custom dims written plural at least once ("users") → render them plural too. */
  private pluralDims = new Set<string>();

  markPlural(dim: string): void {
    this.pluralDims.add(dim);
  }

  pluralSet(): Set<string> {
    return this.pluralDims;
  }

  /** A pure count of one custom dim, e.g. `400 users`. */
  static isPopulation(q: Quantity): boolean {
    if (q.dims.size !== 1) return false;
    const [k, e] = [...q.dims][0]!;
    return e === 1 && baseOf(k) === "custom";
  }

  /** A rate over time with NO custom denominator, e.g. `100m urls/mo`, `500 KB/s`. */
  static isFlow(q: Quantity): boolean {
    let hasTimeDenom = false;
    let hasNumerator = false;
    for (const [k, e] of q.dims) {
      if (e < 0 && baseOf(k) === "custom") return false; // that's a fact
      if (e < 0 && baseOf(k) === "time") hasTimeDenom = true;
      if (e > 0) hasNumerator = true;
    }
    return hasTimeDenom && hasNumerator;
  }

  addFlow(q: Quantity): void {
    for (const [k, e] of q.dims) {
      if (e > 0) {
        this.flowMap.set(k, q); // key by the produced dimension
        return;
      }
    }
  }

  flows(): Quantity[] {
    return [...this.flowMap.values()];
  }

  addPopulation(q: Quantity): void {
    const k = [...q.dims][0]![0];
    this.pops.set(k, q); // last wins
  }

  populations(): Quantity[] {
    return [...this.pops.values()];
  }

  add(q: Quantity): void {
    // pivot = leftmost custom denominator
    let pivot: string | undefined;
    for (const [k, e] of q.dims) {
      if (e < 0 && baseOf(k) === "custom") {
        pivot = k;
        break;
      }
    }
    if (!pivot) {
      throw new CalcError("a fact must define a custom unit (e.g. `500KB/img`)");
    }
    const e = q.dims.get(pivot)!;
    const rhsDims = cloneDims(q.dims);
    rhsDims.delete(pivot);
    // 1 pivot = q * pivot^(-e); since custom base factor is 1, value is unchanged
    const rhs: Quantity = { value: q.value, dims: scaleDims(rhsDims, 1 / -e) };

    const list = this.map.get(pivot) ?? [];
    const sig = signature(rhs.dims);
    const existing = list.find((x) => signature(x.dims) === sig);
    if (existing) existing.value += rhs.value; // additive same-shape facts merge
    else list.push(rhs);
    this.map.set(pivot, list);
  }

  get(dimKey: string): Quantity[] | undefined {
    return this.map.get(dimKey);
  }

  pivots(): string[] {
    return [...this.map.keys()];
  }

  /**
   * A custom dimension that is defined (directly or transitively) in terms of
   * itself — a cycle in the fact graph, i.e. contradictory facts (`leg = 2 arm`,
   * `arm = 3 leg`). Returns its name, or null if the graph is acyclic (a DAG).
   */
  findCycle(): string | null {
    const adj = new Map<string, Set<string>>(); // base(pivot) → bases it expands into
    for (const [pivot, rhsList] of this.map) {
      const from = nameOf(pivot);
      const deps = adj.get(from) ?? new Set<string>();
      for (const rhs of rhsList) {
        for (const [k, e] of rhs.dims) if (e !== 0 && baseOf(k) === "custom") deps.add(nameOf(k));
      }
      adj.set(from, deps);
    }
    const state = new Map<string, number>(); // 1 = on stack, 2 = done
    const walk = (n: string): string | null => {
      state.set(n, 1);
      for (const m of adj.get(n) ?? []) {
        const s = state.get(m);
        if (s === 1) return m; // back-edge → cycle
        if (s === undefined && walk(m)) return m;
      }
      state.set(n, 2);
      return null;
    };
    for (const n of adj.keys()) {
      if (!state.has(n)) {
        const cyc = walk(n);
        if (cyc) return cyc;
      }
    }
    return null;
  }
}

function scaleDims(d: DimMap, k: number): DimMap {
  if (k === 1) return cloneDims(d);
  const out: DimMap = new Map();
  for (const [key, e] of d) out.set(key, e * k);
  return out;
}

// ---- expansion -------------------------------------------------------------

/**
 * Facts that expand dimension `key`. A tagged dim (`melon#cogs`) inherits the
 * untagged base's facts (`$2/melon`) — untagged relationships are shared defaults
 * that every tag uses. An exact `melon#cogs` fact, if stated, wins.
 */
function factsFor(key: string, facts: Facts): Quantity[] | undefined {
  return facts.get(key) ?? (tagOf(key) ? facts.get(nameOf(key)) : undefined);
}

function firstExpandable(q: Quantity, facts: Facts): string | undefined {
  for (const [k, e] of q.dims) {
    if (e > 0 && baseOf(k) === "custom" && factsFor(k, facts)) return k;
  }
  return undefined;
}

function substitute(q: Quantity, key: string, exp: Quantity): Quantity {
  const e = q.dims.get(key)!;
  const tag = tagOf(key); // a consumed tag rides onto whatever the fact produces
  const dims = cloneDims(q.dims);
  dims.delete(key);
  for (const [k, ex] of exp.dims) {
    const rk = tag && !tagOf(k) ? `${k}#${tag}` : k; // transfer tag to untagged outputs
    addExp(dims, rk, ex * e);
  }
  const out: Quantity = { value: q.value * exp.value ** e, dims };
  if (q.time) out.time = q.time;
  return out;
}

/**
 * All reachable states (start, every intermediate, every terminal). Each branch
 * tracks the dimensions it has already expanded; re-expanding one means a fact
 * cycle (`leg = 2 arm`, `arm = 3 leg`) — contradictory facts, so error rather than
 * spin to `∞`. (A DAG, incl. diamonds, never re-expands a base in one lineage.)
 */
function allStates(q: Quantity, facts: Facts): Quantity[] {
  const acc: Quantity[] = [];
  const stack: Array<{ q: Quantity; seen: Set<string> }> = [{ q, seen: new Set() }];
  let guard = 0;
  while (stack.length && guard++ < 10000) {
    const { q: cur, seen } = stack.pop()!;
    acc.push(cur);
    const key = firstExpandable(cur, facts);
    if (!key) continue;
    const base = nameOf(key);
    if (seen.has(base)) {
      throw new CalcError(`cyclic facts: '${base}' is defined in terms of itself`);
    }
    const next = new Set(seen).add(base);
    for (const exp of factsFor(key, facts)!) stack.push({ q: substitute(cur, key, exp), seen: next });
  }
  return acc;
}

function isTerminal(q: Quantity, facts: Facts): boolean {
  return firstExpandable(q, facts) === undefined;
}

function mergeBySignature(list: Quantity[]): Quantity[] {
  const map = new Map<string, Quantity>();
  for (const q of list) {
    const sig = signature(q.dims);
    const ex = map.get(sig);
    if (ex) ex.value += q.value;
    else map.set(sig, { value: q.value, dims: cloneDims(q.dims), time: q.time });
  }
  return [...map.values()];
}

/** Drop tags, collapsing same-base dims (`data#md` + `data#im` → `data`). */
function stripTags(dims: DimMap): DimMap {
  const out: DimMap = new Map();
  for (const [k, e] of dims) {
    const nk = nameOf(k);
    const v = (out.get(nk) ?? 0) + e;
    if (v === 0) out.delete(nk);
    else out.set(nk, v);
  }
  return out;
}

const untag = (q: Quantity): Quantity =>
  q.time ? { value: q.value, dims: stripTags(q.dims), time: q.time } : { value: q.value, dims: stripTags(q.dims) };

const isTagged = (q: Quantity): boolean => [...q.dims].some(([k]) => tagOf(k));

/** Same-shape *tag buckets*: ≥2, identical once tags drop, and every one tagged
 *  (a mix with an untagged contribution would make a bare total ambiguous). */
function areTagBuckets(qs: Quantity[]): boolean {
  if (qs.length < 2 || !qs.every(isTagged)) return false;
  const s0 = signature(stripTags(qs[0]!.dims));
  return qs.every((q) => signature(stripTags(q.dims)) === s0);
}

/**
 * Render a set of results, appending the grand total when it *matters*: only if the
 * quantities are same-shape tag buckets and the rounded total differs from the
 * largest bucket. (`10 TB #img ; 10 GB #md` → total rounds to 10 TB = the max, so it
 * is omitted; `10 TB #img ; 5 TB #md` → 15 TB tips, so it is shown.) Precision-aware
 * for free — the test is on the rendered strings.
 */
function withTotal(qs: Quantity[], renderOne: (q: Quantity) => string): string {
  if (areTagBuckets(qs)) qs = [...qs].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const parts = qs.map(renderOne);
  if (areTagBuckets(qs)) {
    // the total is a final aggregate — flag solved so it isn't re-drilled
    const total: Quantity = { value: qs.reduce((s, q) => s + q.value, 0), dims: stripTags(qs[0]!.dims), solved: true };
    const t = qs.find((q) => q.time)?.time;
    if (t) total.time = t;
    const max = qs.reduce((a, b) => (Math.abs(b.value) > Math.abs(a.value) ? b : a));
    const totalStr = renderOne(total);
    if (totalStr !== renderOne(untag(max))) parts.push(totalStr);
  }
  return parts.join(" ; ");
}

function terminals(q: Quantity, facts: Facts): Quantity[] {
  return mergeBySignature(allStates(q, facts).filter((s) => isTerminal(s, facts)));
}

// ---- universal graph resolution -------------------------------------------
//
// Every relationship is a quantity-vector; a query is "reach a target dimension
// by composing them". Facts compose with each other and with anchors; the result
// is the set of all reachable shapes. A query filters that set for the dimension
// asked, picking the *nearest* (fewest residual custom dims) — so incomplete data
// degrades gracefully (`$10/melon`, `5 melon/person`, `$ ?` → `$50/person`).

/** Reconstruct each stated fact as a standalone quantity (`100 TB/server`). */
function factQuantities(facts: Facts): Quantity[] {
  const out: Quantity[] = [];
  for (const pivot of facts.pivots()) {
    for (const R of facts.get(pivot)!) {
      const dims = cloneDims(R.dims);
      dims.set(pivot, (dims.get(pivot) ?? 0) - 1); // R/pivot
      out.push({ value: R.value, dims, time: R.time });
    }
  }
  return out;
}

function customEntries(q: Quantity): Array<[string, number]> {
  return [...q.dims].filter(([k, e]) => e !== 0 && baseOf(k) === "custom");
}

/** A dim key of the same base as `k` but a *different* tag, with the opposite sign
 *  of `delta` (so adding `delta` cancels it). Used for tag-aware composition. */
function variantToCancel(dims: DimMap, k: string, delta: number): string | undefined {
  const base = nameOf(k);
  for (const [dk, de] of dims) {
    if (dk !== k && de !== 0 && nameOf(dk) === base && Math.sign(de) === -Math.sign(delta)) return dk;
  }
  return undefined;
}

/**
 * Compose a quantity with a fact, tag-aware (`op` = +1 multiply, −1 divide). An
 * exact dim cancels normally; a tagged dim cancels its untagged base (and vice
 * versa), and the consumed tag rides onto whatever the fact produces — the same
 * "untagged is a shared base, tags ride through" rule the drill uses, now in the
 * graph. Returns null on divide-by-zero.
 */
function composeFact(q: Quantity, f: Quantity, op: 1 | -1): Quantity | null {
  if (op === -1 && f.value === 0) return null;
  const dims = cloneDims(q.dims);
  const value = op === 1 ? q.value * f.value : q.value / f.value;
  const consumedTags: string[] = [];
  for (const [k, ef] of f.dims) {
    const delta = ef * op;
    if (delta === 0) continue;
    if (dims.has(k)) {
      addExp(dims, k, delta); // exact-key cancel/add
      continue;
    }
    const variant = variantToCancel(dims, k, delta);
    if (variant) {
      const ct = tagOf(k) ?? tagOf(variant); // a tag on either side is consumed here
      if (ct) consumedTags.push(ct);
      addExp(dims, variant, delta); // cancel into the existing variant
    } else {
      addExp(dims, k, delta);
    }
  }
  // a consumed tag rides onto the surviving untagged numerator(s)
  if (consumedTags.length) {
    const tag = consumedTags[0]!;
    for (const [k, e] of [...dims]) {
      if (e > 0 && k !== "time" && !tagOf(k)) {
        dims.delete(k);
        addExp(dims, `${k}#${tag}`, e);
      }
    }
  }
  const out: Quantity = { value, dims };
  if (q.time) out.time = q.time;
  else if (f.time) out.time = f.time;
  return out;
}

/** Did `cand` shrink at least one dimension that `q` had (custom or base)? A step
 *  must cancel *something* — keeps the graph walk converging, not proliferating. */
function canceledADim(q: Quantity, cand: Quantity): boolean {
  for (const [k, e] of q.dims) {
    if (e !== 0 && Math.abs(cand.dims.get(k) ?? 0) < Math.abs(e)) return true;
  }
  return false;
}

/**
 * All quantity-shapes reachable from `starts` by multiplying/dividing with the
 * stated facts, where each step cancels a shared custom dimension. Deduped by
 * dimensional signature (first/shortest path wins).
 */
function reachableSet(starts: Quantity[], facts: Facts, seedFacts: boolean): Quantity[] {
  const fqs = factQuantities(facts);
  const seen = new Map<string, Quantity>();
  const queue: Quantity[] = [];
  const push = (q: Quantity): void => {
    const s = signature(q.dims);
    if (seen.has(s)) return;
    seen.set(s, q);
    queue.push(q);
  };
  // Facts are always composition *edges*. `seedFacts` also makes each a start, so
  // relationships compose with no anchor (`$/melon` × `melon/person` → `$/person`).
  // For a specific quantity, leave it off so we only surface q-connected shapes.
  for (const s of seedFacts ? [...starts, ...fqs] : starts) push(s);
  let guard = 0;
  while (queue.length && guard++ < 20000) {
    const q = queue.shift()!;
    for (const f of fqs) {
      for (const cand of [composeFact(q, f, 1), composeFact(q, f, -1)]) {
        if (cand && canceledADim(q, cand)) push(cand);
      }
    }
  }
  return [...seen.values()];
}

/** Custom dims that some fact *produces* (has in its numerator) — "downstream". */
function producedDims(facts: Facts): Set<string> {
  const s = new Set<string>();
  for (const f of factQuantities(facts)) {
    for (const [k, e] of f.dims) if (e > 0 && baseOf(k) === "custom") s.add(nameOf(k));
  }
  return s;
}

/**
 * Solve for a target dimension over the relationship graph: the nearest reachable
 * shape carrying it, ranked by fewest residual custom dims (then most-upstream
 * residual). Residuals are kept, not dropped — `$ ?` over `$/melon`,`melon/person`
 * with no person count yields `$50/person`.
 */
export function solveDim(
  starts: Quantity[],
  targetDim: string,
  facts: Facts,
  seedFacts = false,
): Quantity[] {
  const produced = producedDims(facts);
  // A valid answer has the target in its numerator, plus only custom residuals
  // (the "missing data" dims) and an optional `/time`. No leftover base dims
  // (`data`, `money`) and no `time^≠-1` — those are half-cancelled junk.
  const valid = (q: Quantity): boolean => {
    let hasTarget = false;
    for (const [k, e] of q.dims) {
      if (e === 0) continue;
      if (nameOf(k) === targetDim && e > 0) {
        hasTarget = true;
        continue;
      }
      if (baseOf(k) === "custom") continue; // residual custom dim — fine
      if (k === "time" && e === -1) continue; // a per-time rate — fine
      return false;
    }
    return hasTarget;
  };
  // Multiplicative totals first: expand the anchors and SUM same-shape terminals,
  // so a diamond (two paths to one shape) adds up. `reachableSet` dedups by
  // signature (keep-first), which would *drop* a second path — fine for the divide
  // (capacity) and no-anchor (compose) cases, wrong for totals. So fall to the graph
  // walk only when nothing reaches the target multiplicatively.
  let cands = mergeBySignature(starts.flatMap((s) => allStates(s, facts)).filter(valid));
  if (cands.length === 0) cands = reachableSet(starts, facts, seedFacts).filter(valid);
  if (cands.length === 0) return [];
  // Tag laundering: a round-trip in the graph can shed a tag, leaving an *untagged*
  // twin of a tagged result with the same shape and value. Drop it — keep the
  // tagged one (different-value buckets like `40 MB #md ; 80 MB #im` are untouched).
  const nameSig = (q: Quantity): string =>
    signature(new Map([...q.dims].map(([k, e]) => [nameOf(k), e])));
  const isTagged = (q: Quantity): boolean => [...q.dims].some(([k]) => tagOf(k));
  const taggedTwins = cands.filter(isTagged);
  cands = cands.filter(
    (q) =>
      isTagged(q) ||
      !taggedTwins.some(
        (t) => nameSig(t) === nameSig(q) && Math.abs(t.value - q.value) <= Math.abs(q.value) * 1e-9,
      ),
  );
  const score = (q: Quantity): number => {
    const res = customEntries(q).filter(([k]) => nameOf(k) !== targetDim);
    const producedResiduals = res.filter(([k]) => produced.has(nameOf(k))).length;
    return res.length * 10 + producedResiduals;
  };
  // keep every shape at the best (nearest) score — different #tags are distinct
  // buckets, so this preserves multi-output (`gb ?` → `40 MB #md ; 200 MB #im`)
  const best = Math.min(...cands.map(score));
  return cands.filter((q) => score(q) === best).map((q) => ({ ...q, solved: true }));
}

// ---- rendering -------------------------------------------------------------

const COUNT_PREFIX: Array<[string, number]> = [
  ["", 1],
  ["k", 1e3],
  ["M", 1e6],
  ["G", 1e9], // counts top out at giga; past it the mantissa just grows (1000G)
];
const DATA_PREFIX: Array<[string, number]> = [
  ["B", 1],
  ["KB", 1e3],
  ["MB", 1e6],
  ["GB", 1e9],
  ["TB", 1e12],
  ["PB", 1e15],
];
const TIME_TABLE: Array<[string, number]> = [
  ["ms", 0.001],
  ["s", 1],
  ["min", 60],
  ["h", 3600],
  ["d", 86400],
  ["wk", 604800],
  ["mo", 2592000],
  ["y", 31536000],
];

/**
 * Smart rounding. Default (`~{}`) is back-of-envelope: one significant figure, with
 * a half-step when the leading digit is 1–3 (`58→60`, `1.8→2`, `1.4→1.5`, `2.5→2.5`).
 * Precise mode (`={}`) keeps two significant figures (`58→58`, `115740→120000`).
 */
function smartRound(v: number, precise = false): number {
  if (!isFinite(v) || v === 0) return 0;
  const sign = Math.sign(v);
  const k = Math.floor(Math.log10(Math.abs(v)));
  if (precise) {
    const step = 10 ** (k - 1); // 2 significant figures
    return sign * parseFloat((Math.round(Math.abs(v) / step) * step).toPrecision(12));
  }
  const m = Math.abs(v) / 10 ** k; // mantissa in [1, 10)
  const lead = Math.floor(m);
  let rm = lead <= 3 ? Math.round(m * 2) / 2 : Math.round(m); // halves vs integers
  let exp = k;
  if (rm >= 10) {
    rm /= 10;
    exp += 1;
  }
  return sign * parseFloat((rm * 10 ** exp).toPrecision(12));
}

function numStr(n: number, precise = false): string {
  const r = smartRound(n, precise);
  if (!Number.isFinite(r)) return Number.isNaN(r) ? "NaN" : r < 0 ? "-∞" : "∞";
  // plain decimal — never scientific notation (`0.00000004`, not `4e-8`)
  return r.toLocaleString("en-US", { maximumFractionDigits: 20, useGrouping: false });
}

/**
 * Pick a prefix, smart-round the mantissa, renormalise if rounding pushed it to ≥1000.
 * Past the largest prefix the mantissa just grows (`1000000 PB`) — deliberately ugly,
 * so an absurd estimate looks absurd.
 */
function withPrefix(
  value: number,
  table: Array<[string, number]>,
  precise = false,
): { mantissa: number; label: string } {
  // relative epsilon: 999999999.4 (float drift on 1e9) should still pick GB, not MB
  const v = Math.abs(value) * (1 + 1e-9);
  let idx = 0;
  for (let i = 0; i < table.length; i++) {
    if (v >= table[i]![1]) idx = i;
  }
  let mantissa = smartRound(value / table[idx]![1], precise);
  if (mantissa >= 1000 && idx < table.length - 1) {
    idx += 1;
    mantissa = smartRound(value / table[idx]![1], precise); // 999 → 1000 → 1 k
  }
  return { mantissa, label: table[idx]![0]! };
}

function fmtData(value: number, precise: boolean, numUnit?: string): { mantissa: number; label: string } {
  if (numUnit) {
    const m = matchData(numUnit);
    // a bare base byte (`? b` / `? B`) still auto-scales to a readable prefix;
    // an explicit prefixed unit (`? GB`) is honored verbatim.
    if (m && !/^[bB]$/.test(numUnit)) return { mantissa: smartRound(value / m.factor, precise), label: numUnit };
  }
  return withPrefix(value, DATA_PREFIX, precise);
}

function fmtCount(value: number, precise: boolean): { mantissa: number; sym: string } {
  const { mantissa, label } = withPrefix(value, COUNT_PREFIX, precise);
  return { mantissa, sym: label };
}

function fmtTime(value: number, precise: boolean): { mantissa: number; label: string } {
  return withPrefix(value, TIME_TABLE, precise);
}

export interface RenderOpts {
  numUnit?: string;
  /** explicit denominator unit (e.g. `s` from `?/s`, `req` from `?/req`). */
  denomUnit?: string;
  /** coefficient on the denominator, e.g. 5 from `?/5y`, 100 from `?/100req`. */
  denomCoeff?: number;
  /** custom dims to render plural (echoing the author's plural usage). */
  plurals?: Set<string>;
  /** precise mode (`={}`): two significant figures instead of one. */
  precise?: boolean;
}

/** Echo a custom dimension plural when the author wrote it plural (≥3 chars). */
function showName(dim: string, plurals?: Set<string>): string {
  return plurals?.has(dim) && dim.length >= 3 ? `${dim}s` : dim;
}

function unitLabel(dimKey: string, plurals?: Set<string>): string {
  const b = baseOf(dimKey);
  if (b === "data") return "B";
  if (b === "money") return "$";
  if (b === "time") return "s";
  return showName(nameOf(dimKey), plurals);
}

export function render(q: Quantity, opts: RenderOpts = {}): string {
  if (!Number.isFinite(q.value)) return Number.isNaN(q.value) ? "NaN" : q.value < 0 ? "-∞" : "∞";
  const precise = !!opts.precise;
  const num = (n: number): string => numStr(n, precise);
  const entries = [...q.dims].filter(([, e]) => e !== 0);
  const positives = entries.filter(([, e]) => e > 0);
  const negatives = entries.filter(([, e]) => e < 0);
  // format the value against a "main" numerator: prefer data, then money, then custom
  const main =
    positives.find(([k]) => baseOf(k) === "data") ??
    positives.find(([k]) => baseOf(k) === "money") ??
    positives[0];
  // primary denominator (a rate): time if present, else the first one
  const denom = negatives.find(([k]) => baseOf(k) === "time") ?? negatives[0];

  let value = q.value;
  let denomLabel: string | undefined;
  if (denom) {
    const dk = denom[0];
    const coeff = opts.denomCoeff ?? 1;
    if (baseOf(dk) === "time") {
      const unit = opts.denomUnit && resolveUnit(opts.denomUnit).family === "time" ? opts.denomUnit : q.time || "s";
      value *= (TIME[unit] ?? 1) * coeff;
      denomLabel = coeff !== 1 ? `${num(coeff)}${unit}` : unit; // `/5y`
    } else {
      value *= coeff; // custom/data/money denominator (base factor 1) → per N
      const nm = unitLabel(dk, opts.plurals);
      denomLabel = coeff !== 1 ? `${num(coeff)}${nm}` : nm; // `/100req`
    }
  }

  let body: string;
  let mainTag: string | undefined;

  if (!main) {
    // pure scalar / dimensionless count
    const { mantissa, sym } = fmtCount(value, precise);
    body = `${num(mantissa)}${sym}`;
  } else {
    mainTag = tagOf(main[0]);
    const base = baseOf(main[0]);
    if (base === "data") {
      // bit family only when explicitly requested
      if (opts.numUnit && /bit$/i.test(opts.numUnit)) {
        const m = matchData(opts.numUnit);
        if (m) body = `${num(value / m.factor)} ${opts.numUnit}`;
        else body = `${num(value)} B`;
      } else {
        const { mantissa, label } = fmtData(value, precise, opts.numUnit);
        body = `${num(mantissa)} ${label}`;
      }
    } else if (base === "money") {
      const { mantissa, sym } = fmtCount(value, precise);
      body = `$${num(mantissa)}${sym}`;
    } else if (base === "time") {
      const ru = opts.numUnit ? resolveUnit(opts.numUnit) : undefined;
      if (ru && ru.family === "time") {
        body = `${num(value / ru.factor)} ${opts.numUnit}`; // honor `? d`, `? y`
      } else {
        const { mantissa, label } = fmtTime(value, precise);
        body = `${num(mantissa)} ${label}`;
      }
    } else {
      const { mantissa, sym } = fmtCount(value, precise);
      body = `${num(mantissa)}${sym} ${showName(nameOf(main[0]), opts.plurals)}`;
    }
  }

  if (denom) body += `/${denomLabel}`;
  if (mainTag) body += ` #${mainTag}`;

  // every remaining dimension is shown explicitly — never silently dropped
  for (const [k, e] of entries) {
    if (main && k === main[0]) continue;
    if (denom && k === denom[0]) continue;
    const label = unitLabel(k, opts.plurals);
    const tag = tagOf(k);
    for (let i = 0; i < Math.abs(e); i++) body += e > 0 ? ` ${label}` : `/${label}`;
    if (tag) body += ` #${tag}`;
  }
  return body;
}

// ---- derivation tree (the `**` "show your work" view) ----------------------

interface DerivNode {
  q: Quantity;
  via?: string; // the fact applied to reach this node from its parent
  children: DerivNode[];
}

function deriveTree(q: Quantity, facts: Facts, depth = 0, seen: Set<string> = new Set()): DerivNode {
  const node: DerivNode = { q, children: [] };
  if (depth > 50) return node;
  const key = firstExpandable(q, facts);
  if (key) {
    const base = nameOf(key);
    if (seen.has(base)) throw new CalcError(`cyclic facts: '${base}' is defined in terms of itself`);
    const next = new Set(seen).add(base);
    for (const exp of factsFor(key, facts)!) {
      const child = deriveTree(substitute(q, key, exp), facts, depth + 1, next);
      child.via = `${render(exp)}/${nameOf(key)}`;
      node.children.push(child);
    }
  }
  return node;
}

/** Flatten the tree into a compact list: each derived step with the fact behind it. */
function flattenDeriv(
  node: DerivNode,
  out: string[],
  isRoot: boolean,
  plurals?: Set<string>,
  precise = false,
): void {
  if (!isRoot) out.push(`${render(node.q, { plurals, precise })}${node.via ? ` (via ${node.via})` : ""}`);
  for (const c of node.children) flattenDeriv(c, out, false, plurals, precise);
}

// ---- resolve a query against facts -----------------------------------------

function hasNumDim(q: Quantity, dim: string, tag?: string): boolean {
  for (const [k, e] of q.dims) {
    if (e > 0 && nameOf(k) === dim && (tag === undefined || tagOf(k) === tag)) return true;
  }
  return false;
}

/** Does the quantity have a custom unit in the denominator (→ it's a rate fact)? */
export function hasCustomDenominator(q: Quantity): boolean {
  for (const [k, e] of q.dims) if (e < 0 && baseOf(k) === "custom") return true;
  return false;
}

/** The demand context: populations and flows in scope, expanded through facts. */
function contextDemands(facts: Facts): Quantity[] {
  const out: Quantity[] = [];
  for (const anchor of [...facts.populations(), ...facts.flows()]) {
    for (const st of allStates(anchor, facts)) out.push(st);
  }
  return out;
}

/**
 * Fold the demand context *into* a query LHS: for each custom denominator, if a
 * demand produces that dim, multiply it in (per-user × users → total; per-user ×
 * user/y → growth rate). The complementary direction — dividing a demand by a
 * per-X capacity to *expose* X — lives in `resolve` (`solveForX`), since it needs
 * the query's target dimension.
 */
export function foldDemand(q0: Quantity, facts: Facts): Quantity {
  const demands = contextDemands(facts);
  if (demands.length === 0) return q0;
  let q = q0;
  for (let guard = 0; guard < 8; guard++) {
    let moved = false;
    for (const [k, e] of [...q.dims]) {
      if (e >= 0 || baseOf(k) !== "custom") continue;
      const provider = demands.find((d) => (d.dims.get(k) ?? 0) > 0);
      if (provider) {
        for (let i = 0; i < -e; i++) q = mul(q, provider);
        moved = true;
        break;
      }
    }
    if (!moved) break;
  }
  return q;
}

/** The first custom dimension that appears as a denominator, if any. */
function firstCustomDenom(q: Quantity): string | undefined {
  for (const [k, e] of q.dims) if (e < 0 && baseOf(k) === "custom") return nameOf(k);
  return undefined;
}

/**
 * If `q` is a duration (a positive time dimension) and flows are stated, multiply
 * it by each flow → the amount each flow produces over that duration (separate
 * outputs). Otherwise the quantity is returned unchanged.
 */
export function applyFlows(q: Quantity, facts: Facts): Quantity[] {
  const tExp = q.dims.get("time");
  const flows = facts.flows();
  if (tExp !== undefined && tExp > 0 && flows.length > 0) {
    return flows.map((f) => mul(q, f));
  }
  return [q];
}

/** Evaluate a query LHS and fold in stated anchors → the quantities to resolve. */
export function prepareQuantities(exprStr: string, facts: Facts): Quantity[] {
  const q = foldDemand(evalExpr(parseExpression(exprStr)), facts);
  return applyFlows(q, facts);
}

/** Do `q` and flow `f` share a dimension with opposite signs (so `q*f` cancels it)? */
function cancels(q: Quantity, f: Quantity): boolean {
  for (const [d, e] of f.dims) {
    const qe = q.dims.get(d);
    if (qe !== undefined && qe !== 0 && e !== 0 && Math.sign(qe) === -Math.sign(e)) return true;
  }
  return false;
}

/**
 * When a query can't resolve directly, try multiplying by a flow that cancels a
 * dimension and retry — e.g. `100 reads/url = ?/s` becomes reads/mo (via the
 * `urls/mo` flow) and then reads/s. Churn through whichever flow makes it work.
 */
function flowRetry(q: Quantity, spec: QuerySpec, facts: Facts): string | null {
  for (const f of facts.flows()) {
    if (!cancels(q, f)) continue;
    try {
      return resolve(mul(q, f), spec, facts);
    } catch {
      /* this flow didn't help */
    }
  }
  return null;
}

/** Resolve several candidate quantities against one spec; keep the ones that work. */
export function resolveQuantities(qs: Quantity[], spec: QuerySpec, facts: Facts): string {
  if (areTagBuckets(qs)) qs = [...qs].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const out: string[] = [];
  let lastErr: unknown;
  for (const q of qs) {
    try {
      out.push(resolve(q, spec, facts));
    } catch (e) {
      const retried = flowRetry(q, spec, facts);
      if (retried !== null) out.push(retried);
      else lastErr = e;
    }
  }
  if (out.length === 0) throw lastErr ?? new CalcError("no result for query");
  // multiple same-shape tag buckets (e.g. `gb ?` → `… #md ; … #im`): append the
  // grand total when it tips the rounded number (see `withTotal`).
  if (areTagBuckets(qs)) {
    const total: Quantity = { value: qs.reduce((s, q) => s + q.value, 0), dims: stripTags(qs[0]!.dims), solved: true };
    const t = qs.find((q) => q.time)?.time;
    if (t) total.time = t;
    const max = qs.reduce((a, b) => (Math.abs(b.value) > Math.abs(a.value) ? b : a));
    const maxU: Quantity = { ...untag(max), solved: true };
    try {
      const totalStr = resolve(total, spec, facts);
      if (totalStr !== resolve(maxU, spec, facts)) out.push(totalStr);
    } catch {
      /* total not expressible under this spec → just show buckets */
    }
  }
  return out.join(" ; ");
}

export function resolve(q: Quantity, spec: QuerySpec, facts: Facts): string {
  const plurals = facts.pluralSet();
  const precise = facts.precise;
  // a capacity-solved quantity is already the answer — don't drill it further
  if (q.solved && (spec.kind === "auto" || spec.kind === "all")) {
    return render(q, { plurals, precise });
  }
  if (spec.kind === "auto") {
    // a leftover per-X capacity → solve "how many X" over the relationship graph
    const denomX = firstCustomDenom(q);
    if (denomX) {
      const solved = solveDim([q, ...facts.populations(), ...facts.flows()], denomX, facts);
      if (solved.length) return withTotal(solved, (s) => render(s, { plurals, precise }));
    }
    return withTotal(terminals(q, facts), (s) => render(s, { plurals, precise }));
  }
  if (spec.kind === "all") {
    const out: string[] = [];
    flattenDeriv(deriveTree(q, facts), out, true, plurals, precise);
    return out.length ? out.join(" ; ") : render(q, { plurals, precise });
  }

  // unit spec
  const factors: SpecFactor[] = spec.factors;
  // percentage output: render a dimensionless ratio as a percent
  if (factors.some((f) => f.unit === "%")) {
    return `${numStr(q.value * 100, precise)}%`;
  }
  const numFactor = factors.find((f) => f.op === "*" && f.unit !== "?");
  const denomFactor = factors.find((f) => f.op === "/");
  const tagFilter =
    factors.map((f) => f.tag).find((t) => t !== undefined) ?? numFactor?.tag;
  const denomUnit = denomFactor && denomFactor.unit !== "?" ? denomFactor.unit : undefined;
  const denomCoeff = denomFactor?.coeff;
  const denomDim = denomUnit ? resolveUnit(denomUnit).dim : undefined;

  // Asking for a per-<unit> rate (`?/s`, `?/req`) only makes sense when the result
  // is a rate over a matching dimension; otherwise there's nothing to divide by.
  const guardDenom = (s: Quantity): Quantity => {
    if (denomUnit === undefined) return s;
    const negs = [...s.dims].filter(([, e]) => e < 0);
    if (negs.length === 0) throw new CalcError(`cannot express per ${denomUnit} — there is no rate here`);
    if (!negs.some(([k]) => nameOf(k) === denomDim)) {
      throw new CalcError(`cannot express per ${denomUnit} here`);
    }
    return s;
  };

  if (numFactor) {
    const dim = resolveUnit(numFactor.unit).dim;
    const states = mergeBySignature(
      allStates(q, facts).filter((s) => hasNumDim(s, dim, numFactor.tag)),
    );
    if (states.length > 0) {
      return withTotal(states, (s) =>
        render(guardDenom(s), { numUnit: numFactor.unit, denomUnit, denomCoeff, plurals, precise }),
      );
    }
    // can't reach the dim by forward expansion → solve it over the relationship graph.
    // Pull in the anchors only when `q` is itself a per-<dim> capacity (so a demand
    // can be divided into it); otherwise stay anchored to `q` so we don't surface a
    // shape that's reachable from the anchors but unrelated to this quantity.
    const qIsCapacity = customEntries(q).some(([k, e]) => nameOf(k) === dim && e < 0);
    const starts = qIsCapacity ? [q, ...facts.populations(), ...facts.flows()] : [q];
    const solved = solveDim(starts, dim, facts);
    if (solved.length) {
      return withTotal(solved, (s) => render(guardDenom(s), { denomUnit, denomCoeff, plurals, precise }));
    }
    throw new CalcError(`cannot express in ${numFactor.unit}`);
  }

  // numerator wildcard / absent → auto terminals, optional tag filter, denom override.
  // a solved total keeps its own numerator (don't drill `req/s` on to bytes).
  let terms = q.solved ? [q] : terminals(q, facts);
  if (tagFilter) terms = terms.filter((s) => [...s.dims].some(([k]) => tagOf(k) === tagFilter));
  if (terms.length === 0) throw new CalcError("no result for query");
  return withTotal(terms, (s) => render(guardDenom(s), { denomUnit, denomCoeff, plurals, precise }));
}
